import React from "react";
import { PiWaveform, PiTextAlignLeft, PiVideoCamera } from "react-icons/pi";
import { SiX, SiLinkedin, SiDiscord } from "react-icons/si";

const COLOR_HEXES = {
  ssblue:   "#3E8DFF",
  ssgreen:  "#3EB567",
  ssorange: "#FF8F47",
  sspink:   "#FF7AAC",
  ssyellow: "#FFD23A",
};
const COLORS = ["ssblue", "ssgreen", "ssorange", "sspink", "ssyellow"];
const TAPE_COLORS = [
  "rgba(255,212,58,0.85)",    // yellow
  "rgba(255,122,172,0.85)",   // pink
  "rgba(127,206,251,0.85)",   // sky
  "rgba(183,242,76,0.85)",    // lime
];

const kindIcon = (proof) => {
  if (proof.source_platform === "Discord")   return <SiDiscord className="h-4 w-4" />;
  if (proof.source_platform === "LinkedIn")  return <SiLinkedin className="h-4 w-4" />;
  if (proof.source_platform === "Twitter/X") return <SiX className="h-4 w-4" />;
  if (proof.kind === "video") return <PiVideoCamera className="h-4 w-4" />;
  if (proof.kind === "audio") return <PiWaveform className="h-4 w-4" />;
  return <PiTextAlignLeft className="h-4 w-4" />;
};

const ProofRow = ({ initProof, index }) => {
  const proof = initProof;
  const manageUrl = `/projects/${proof.project_id}/proofs/${proof.id}/manage`;
  const color = COLORS[index % COLORS.length];
  const hex = COLOR_HEXES[color];
  const tapeColor = TAPE_COLORS[index % TAPE_COLORS.length];
  const rotClass = index % 2 === 0 ? "zz-rot-l" : "zz-rot-r";

  return (
    <a
      href={manageUrl}
      className={`relative block zz-card hoverable p-5 ${rotClass} no-underline`}
      style={{ color: "inherit" }}
    >
      <div className="zz-tape" style={{ background: tapeColor }}></div>

      <div className="flex flex-col sm:flex-row items-start sm:items-start gap-3 sm:gap-4">
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div
            className="h-16 w-16 sm:h-20 sm:w-20 rounded-xl border-[3px] border-black shrink-0 overflow-hidden flex items-center justify-center shadow-[3px_3px_0_#111]"
            style={{ background: hex }}
          >
            {proof.avatar ? (
              <img src={proof.avatar} alt={proof.name} className="h-full w-full object-cover" />
            ) : (
              <span className="text-white font-black text-2xl sm:text-3xl">
                {(proof.name || "?")[0].toUpperCase()}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap sm:hidden">
            <span className={`zz-chip ${proof.approved ? "green" : "yellow"}`}>
              {proof.approved ? "approved" : "waiting"}
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-slate-500 font-medium">
              {kindIcon(proof)}
              <span className="capitalize">{proof.kind}</span>
            </span>
          </div>
        </div>

        <div className="flex-1 min-w-0 w-full">
          <div className="hidden sm:flex items-center gap-2 flex-wrap">
            <span className={`zz-chip ${proof.approved ? "green" : "yellow"}`}>
              {proof.approved ? "approved" : "waiting"}
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-slate-500 font-medium">
              {kindIcon(proof)}
              <span className="capitalize">{proof.kind}</span>
            </span>
          </div>
          <h3 className="zz-display text-xl sm:text-2xl font-black leading-tight mt-3 sm:mt-2 line-clamp-2">
            {proof.headline}
          </h3>
          <p className="text-sm text-slate-600 mt-1 truncate">
            <strong>{proof.name}</strong>
            {proof.contact_title && <span className="text-slate-500"> · {proof.contact_title}</span>}
            {proof.contact_company_name && (
              <span className="text-slate-500"> @ {proof.contact_company_name}</span>
            )}
          </p>
        </div>
      </div>

      {proof.snippet && (
        <div
          className="mt-4 text-sm text-slate-700 prose-sm line-clamp-3"
          dangerouslySetInnerHTML={{ __html: proof.snippet }}
        />
      )}

      <div className="mt-5 pt-3 border-t-[2px] border-dashed border-slate-300 flex items-center justify-between text-xs">
        <time className="text-slate-500 font-bold" dateTime={proof.given_at_utc}>
          {proof.given_at_utc}
        </time>
        <span className="zz-hand text-lg text-ssblue">manage →</span>
      </div>
    </a>
  );
};

export default ProofRow;
